import apiClient, { type RequestConfig } from './client';
import type {
  BatchDetailResponse,
  BatchListResponse,
  NextBatchRequest,
  NextBatchResponse,
  NormalizeLabelsRequest,
  NormalizeLabelsResponse,
  PredictiveProjectCreateRequest,
  PredictiveProjectResponse,
  ProjectMetricsResponse,
  StopControlResponse,
  TriggerScoreRequest,
  TriggerScoreResponse,
  TriggerTrainResponse,
} from './review-predictive-types';

const ENDPOINT = '/review/predictive';

export const reviewPredictiveApi = {
  async createProject(
    request: PredictiveProjectCreateRequest,
    config?: RequestConfig,
  ): Promise<PredictiveProjectResponse> {
    const { data } = await apiClient.post<PredictiveProjectResponse>(
      `${ENDPOINT}/projects`,
      request,
      config,
    );
    return data;
  },

  async getProject(projectId: string, config?: RequestConfig): Promise<PredictiveProjectResponse> {
    const { data } = await apiClient.get<PredictiveProjectResponse>(
      `${ENDPOINT}/projects/${projectId}`,
      config,
    );
    return data;
  },

  async normalizeLabels(
    projectId: string,
    request: NormalizeLabelsRequest,
    config?: RequestConfig,
  ): Promise<NormalizeLabelsResponse> {
    const { data } = await apiClient.post<NormalizeLabelsResponse>(
      `${ENDPOINT}/projects/${projectId}/normalize-labels`,
      request,
      config,
    );
    return data;
  },

  async triggerTrain(projectId: string, config?: RequestConfig): Promise<TriggerTrainResponse> {
    const { data } = await apiClient.post<TriggerTrainResponse>(
      `${ENDPOINT}/projects/${projectId}/train`,
      {},
      config,
    );
    return data;
  },

  async triggerScore(
    projectId: string,
    request: TriggerScoreRequest,
    config?: RequestConfig,
  ): Promise<TriggerScoreResponse> {
    const { data } = await apiClient.post<TriggerScoreResponse>(
      `${ENDPOINT}/projects/${projectId}/score`,
      request,
      config,
    );
    return data;
  },

  async triggerNextBatch(
    projectId: string,
    request: NextBatchRequest,
    config?: RequestConfig,
  ): Promise<NextBatchResponse> {
    const { data } = await apiClient.post<NextBatchResponse>(
      `${ENDPOINT}/projects/${projectId}/next-batch`,
      request,
      config,
    );
    return data;
  },

  async getMetrics(projectId: string, config?: RequestConfig): Promise<ProjectMetricsResponse> {
    const { data } = await apiClient.get<ProjectMetricsResponse>(
      `${ENDPOINT}/projects/${projectId}/metrics`,
      config,
    );
    return data;
  },

  async listBatches(
    projectId: string,
    config?: RequestConfig,
  ): Promise<BatchListResponse> {
    const { data } = await apiClient.get<BatchListResponse>(
      `${ENDPOINT}/projects/${projectId}/batches`,
      config,
    );
    return data;
  },

  async getBatchDetail(
    projectId: string,
    batchId: string,
    config?: RequestConfig,
  ): Promise<BatchDetailResponse> {
    const { data } = await apiClient.get<BatchDetailResponse>(
      `${ENDPOINT}/projects/${projectId}/batches/${batchId}`,
      config,
    );
    return data;
  },

  async triggerStopControl(
    projectId: string,
    config?: RequestConfig,
  ): Promise<StopControlResponse> {
    const { data } = await apiClient.post<StopControlResponse>(
      `${ENDPOINT}/projects/${projectId}/stop-control`,
      {},
      config,
    );
    return data;
  },
};
